import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client/react'

import { AttendeeOrderBy, OrderDirection } from '@pleio/gql'

import FetchMore from 'js/components/FetchMore'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import SearchBar from 'js/components/SearchBar/SearchBar'
import Table from 'js/components/Table/Table'
import type { OrderDirectionType } from 'js/components/Table/Table.types'
import TableSortableHeader from 'js/components/TableSortableHeader/TableSortableHeader'
import Text from 'js/components/Text/Text'
import { onlyTypename } from 'js/lib/helpers/onlyTypename'

import { GET_EVENT_GUEST_LIST_WRAPPER } from './GuestList.queries'
import { CHECK_IN_GUEST } from './List.mutations'
import { GET_EVENT_GUEST_LIST } from './List.queries'
import ListItem from './ListItem'

type Props = {
    guid: string
    state: string | null
}

const List = ({ guid, state }: Props) => {
    const { t } = useTranslation()

    const [checkInGuest] = useMutation(CHECK_IN_GUEST)

    const isCheckedIn = state === null

    useEffect(() => {
        setQ('')
        setSearchInput('')
    }, [state])

    const [orderBy, setOrderBy] = useState<AttendeeOrderBy>(
        isCheckedIn ? AttendeeOrderBy.TimeCheckedIn : AttendeeOrderBy.Name,
    )
    const [orderDirection, setOrderDirection] =
        useState<OrderDirectionType>('desc')

    const PAGE_SIZE = 2
    const [searchInput, setSearchInput] = useState('')

    const onChangeSearchInput = (evt) => {
        setSearchInput(evt.target.value)
    }

    const [q, setQ] = useState('')
    const onSubmitSearchInput = (evt) => {
        evt.preventDefault()
        setQ(searchInput)
    }

    const { loading, data, fetchMore } = useQuery(GET_EVENT_GUEST_LIST, {
        variables: {
            query: q,
            guid,
            state,
            isCheckedIn,
            offset: 0,
            limit: PAGE_SIZE,
            orderBy,
            orderDirection:
                orderDirection === 'asc'
                    ? OrderDirection.Asc
                    : OrderDirection.Desc,
        },
    })

    const toggleCheckIn = (emailAddress: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            checkInGuest({
                variables: {
                    input: {
                        guid,
                        emailAddress,
                        timeCheckedIn: isCheckedIn
                            ? null
                            : new Date().toISOString(),
                    },
                },
                refetchQueries: [
                    GET_EVENT_GUEST_LIST,
                    GET_EVENT_GUEST_LIST_WRAPPER,
                ],
            })
                .then(() => {
                    resolve()
                })
                .catch((errors) => {
                    reject(new Error(errors))
                })
        })
    }

    const event = onlyTypename(data?.entity, 'Event')

    const attendees = event?.attendees

    const listConfig = attendees
        ? {
              null: {
                  total: attendees.totalCheckedIn,
                  messageKey: 'entity-event.checked-in-no-results',
              },
              accept: {
                  total: attendees.totalAcceptNotCheckedIn,
                  messageKey: 'entity-event.going-no-results',
              },
              waitinglist: {
                  total: attendees.totalWaitinglistNotCheckedIn,
                  messageKey: 'entity-event.queue-no-results',
              },
          }[state]
        : undefined

    if (!loading && listConfig?.total === 0) {
        return (
            <Text
                textAlign="center"
                style={{ marginTop: '40px' }}
                role="status"
            >
                {t(listConfig.messageKey)}
            </Text>
        )
    }

    return (
        <>
            <form method="GET" onSubmit={onSubmitSearchInput}>
                <SearchBar
                    name="search-attendee"
                    value={searchInput}
                    onChange={onChangeSearchInput}
                    label={t('entity-event.search-attendee')}
                    size="large"
                    borderStyle="rounded"
                    style={{ maxWidth: '480px', margin: '20px auto 8px' }}
                />
            </form>

            {loading ? (
                <LoadingSpinner style={{ marginTop: '40px' }} />
            ) : (
                <Table style={{ marginTop: '24px' }}>
                    <thead>
                        <tr>
                            <TableSortableHeader<AttendeeOrderBy>
                                name={AttendeeOrderBy.Name}
                                label={t('global.name')}
                                orderBy={orderBy}
                                orderDirection={orderDirection}
                                setOrderBy={setOrderBy}
                                setOrderDirection={setOrderDirection}
                                style={{ paddingLeft: '52px' }}
                            />

                            <TableSortableHeader<AttendeeOrderBy>
                                name={AttendeeOrderBy.Email}
                                label={t('form.email-address')}
                                orderBy={orderBy}
                                orderDirection={orderDirection}
                                setOrderBy={setOrderBy}
                                setOrderDirection={setOrderDirection}
                            />

                            {isCheckedIn && (
                                <TableSortableHeader<AttendeeOrderBy>
                                    name={AttendeeOrderBy.TimeCheckedIn}
                                    label={t('entity-event.checked-in')}
                                    orderBy={orderBy}
                                    orderDirection={orderDirection}
                                    setOrderBy={setOrderBy}
                                    setOrderDirection={setOrderDirection}
                                    align="right"
                                />
                            )}
                        </tr>
                    </thead>

                    <FetchMore
                        as="tbody"
                        edges={attendees?.edges}
                        getMoreResults={(data) =>
                            data?.entity?.attendees?.edges
                        }
                        fetchMore={fetchMore}
                        fetchCount={50}
                        maxLimit={listConfig?.total}
                    >
                        {attendees?.edges.map((attendee, i) => {
                            return (
                                <ListItem
                                    key={`${attendee.email}-${i}`}
                                    entity={attendee}
                                    isCheckedIn={isCheckedIn}
                                    onToggleCheckIn={toggleCheckIn}
                                />
                            )
                        })}
                    </FetchMore>
                </Table>
            )}
        </>
    )
}

export default List
